import { DEMO_BANNER_TEXT, DEMO_BANNER_TEXT_KEPT, DEMO_ROLES, DEMO_ROLE_LABELS, DemoRole } from '@garage/shared';
import { Link } from '@tanstack/react-router';
import { useId } from 'react';
import { Button } from '../../../ui/Button.js';
import { cx } from '../../../ui/cx.js';
import { LogOutIcon, PaletteIcon } from '../../../ui/icons.js';
import { useSwitchDemoRole } from '../../lib/demo.js';
import { errorMessage } from '../../lib/errors.js';
import { useActiveMe } from '../../lib/me.js';
import { GP, useGaragePath } from '../../lib/paths.js';
import { useLogout } from '../../lib/session.js';

/** Mention ajoutée au texte du contrat : aucune sortie réelle depuis un garage de démonstration. */
export const DEMO_NO_EMAIL_TEXT = 'aucun e-mail n’est envoyé';

/** Texte complet du bandeau (contrat partagé, puis la mention des e-mails). */
export function demoBannerText(resetNightly: boolean | null | undefined): string {
  return `${resetNightly === false ? DEMO_BANNER_TEXT_KEPT : DEMO_BANNER_TEXT} · ${DEMO_NO_EMAIL_TEXT}`;
}

/**
 * Bandeau permanent d'une session de démonstration (vague 7), discret mais toujours visible, sous
 * l'en-tête. Une ligne au téléphone : « Démonstration », le rôle (liste native, au pouce) et « Quitter ».
 * Le texte complet (données fictives, réinitialisation, aucun e-mail) est lu par les lecteurs d'écran et
 * affiché en entier sur ordinateur. « Quitter » ferme la session de démonstration (`auth.logout`).
 */
export function DemoBanner() {
  const me = useActiveMe();
  const logout = useLogout();
  const switchRole = useSwitchDemoRole();
  const selectId = useId();
  const gp = useGaragePath();
  if (!me.demo) return null;
  // 02/10/2026 : apparence propre à la visite (mode, couleurs, nom, logo), modifiable avec le droit de la marque.
  const canBrand = me.active.permissions.includes('settings.manage');
  const text = demoBannerText(me.demoResetNightly);
  const role = DemoRole.safeParse(me.active.role);
  return (
    <div
      role="region"
      aria-label="Démonstration"
      className="border-b border-warning-line bg-warning-bg px-[var(--page-x)] py-1 text-warning-fg lg:py-1.5"
    >
      <div className="mx-auto flex max-w-[1360px] items-center gap-2 lg:gap-4">
        <p className="min-w-0 flex-1 truncate text-sm">
          <span className="font-medium">Démonstration</span>
          <span className="hidden sm:inline"> · {text.replace(/^Démonstration · /, '')}</span>
          <span className="sr-only sm:hidden"> · {text.replace(/^Démonstration · /, '')}</span>
        </p>
        {role.success ? (
          <span className="flex shrink-0 items-center gap-1.5">
            <label htmlFor={selectId} className="hidden text-sm sm:inline">
              Rôle
            </label>
            <select
              id={selectId}
              aria-label="Rôle de démonstration"
              value={role.data}
              disabled={switchRole.isPending}
              onChange={(e) => {
                const next = DemoRole.safeParse(e.target.value);
                if (next.success && next.data !== role.data) switchRole.mutate(next.data);
              }}
              className={cx(
                'min-h-(--ctl-sm) rounded-lg border border-warning-line bg-surface px-2 text-sm text-ink',
                'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
              )}
            >
              {DEMO_ROLES.map((r) => (
                <option key={r} value={r}>
                  {DEMO_ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </span>
        ) : null}
        {canBrand ? (
          <Link
            to={gp(GP.compteSection('marque'))}
            className={cx(
              'inline-flex min-h-(--ctl-sm) shrink-0 items-center gap-1.5 rounded-lg px-1.5 text-sm font-medium underline-offset-2 hover:underline',
              'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
            )}
          >
            <PaletteIcon size={16} />
            <span className="sr-only lg:not-sr-only">Personnaliser l’apparence</span>
          </Link>
        ) : null}
        <Button size="sm" variant="secondary" icon={<LogOutIcon size={16} />} onClick={() => void logout()}>
          Quitter
        </Button>
      </div>
      {switchRole.error ? (
        <p role="alert" className="mx-auto max-w-[1360px] pb-1 text-sm font-medium">
          {errorMessage(switchRole.error)}
        </p>
      ) : null}
    </div>
  );
}
